import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, ButtonBase, IconButton, Portal, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { usePermissions } from 'react-admin';

import actions, { Actions } from '../../../constants/actions';
import { FLOATING_ELEVATION } from '../../../theme/shadows';
import focusRing from '../../../theme/focus';
import hasAccess from '../../../utilities/hasAccess';
import usePinguChat from '../../../hooks/usePinguChat';
import useResizablePanel from '../../../hooks/useResizablePanel';
import PinguChatWindow from '../../pingu/PinguChatWindow';
import { useDockContext } from '../../pingu/PinguDockContext';
import { DOCKED_PANEL_WIDTH, MIN_PANEL_WIDTH } from '../../pingu/dockedPanelWidth';
import panelModes from '../../pingu/panelModes';
import pillLabel from './pillLabel';
import pingwinMarkIcon from '../../../assets/logos/pingwin-mark.png';

const INITIAL_PANEL_SIZE = { width: DOCKED_PANEL_WIDTH, height: 520 };
const MIN_PANEL_SIZE = { width: MIN_PANEL_WIDTH, height: 360 };

const PinguChatLauncher = () => {
  const [unseenReply, setUnseenReply] = useState(false);
  const [pillDismissed, setPillDismissed] = useState(false);
  const {
    open, setOpen, mode, setMode, canDock, docked,
  } = useDockContext();

  const { permissions } = usePermissions<Actions[]>();
  // The conversation outlives the panel, which is unmounted while it is closed.
  const chat = usePinguChat();
  const {
    size, position, startMove, startResize,
  } = useResizablePanel(INITIAL_PANEL_SIZE, MIN_PANEL_SIZE);
  const wasLoading = useRef(chat.loading);

  useEffect(() => {
    if (wasLoading.current && !chat.loading && !open) setUnseenReply(true);
    if (!wasLoading.current && chat.loading) setPillDismissed(false);
    wasLoading.current = chat.loading;
  }, [chat.loading, open]);

  if (!hasAccess(permissions, actions.PINGU_CHAT_USE)) return null;

  const handleOpen = () => {
    setOpen(true);
    setUnseenReply(false);
    setPillDismissed(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const label = pillLabel(chat.loading, chat.messages[chat.messages.length - 1]);
  const showPill = !open && !pillDismissed && (chat.loading || unseenReply);

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleOpen}
        startIcon={(
          <Box
            component="img"
            src={pingwinMarkIcon}
            alt=""
            sx={{ width: 22, height: 22, display: 'block' }}
          />
        )}
        sx={{
          mr: 2,
          '&:focus-visible': { boxShadow: focusRing },
        }}
      >
        Ask Pingwin
      </Button>

      {open && (
        <Portal>
          <PinguChatWindow
            chat={chat}
            size={size}
            position={position}
            minSize={MIN_PANEL_SIZE}
            onStartMove={startMove}
            onStartResize={startResize}
            onClose={handleClose}
            mode={docked ? panelModes.DOCKED : panelModes.FLOATING}
            onToggleMode={canDock
              ? () => setMode(mode === panelModes.DOCKED ? panelModes.FLOATING : panelModes.DOCKED)
              : undefined}
          />
        </Portal>
      )}

      {showPill && (
        <Portal>
          <Box
            aria-live="polite"
            sx={{
              position: 'fixed',
              right: 16,
              bottom: 16,
              zIndex: (theme) => theme.zIndex.pinguPanel,
              display: 'flex',
              alignItems: 'center',
              borderRadius: 999,
              border: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              boxShadow: (theme) => theme.shadows[FLOATING_ELEVATION],
            }}
          >
            <ButtonBase
              onClick={handleOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                pl: 2,
                pr: 2,
                py: 1,
                borderRadius: 999,
                '&:hover': { backgroundColor: 'background.default' },
                '&:focus-visible': { boxShadow: focusRing },
              }}
            >
              <Box component="img" src={pingwinMarkIcon} alt="" sx={{ width: 16, height: 16, display: 'block' }} />
              <Typography variant="body1" color="text.primary">{label}</Typography>
            </ButtonBase>
            <IconButton
              aria-label="Dismiss"
              onClick={() => setPillDismissed(true)}
              sx={{
                width: 24,
                height: 24,
                mr: 1,
                color: 'text.secondary',
                '&:focus-visible': { boxShadow: focusRing },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Portal>
      )}
    </>
  );
};

export default PinguChatLauncher;
